<template>
  <DialogBase
    :visible.sync="visible"
    :title="dialogTitle"
    size="lg"
    :show-confirm="false"
    cancel-text="Đóng"
    :use-panel="false"
    @cancel="handleClose"
  >
    <div v-if="studentData" class="evl-detail">
      <!-- Header: Student info -->
      <div class="sk-panel evl-detail-student-panel">
        <div class="sk-panel-head">
          <div class="sk-panel-icon">
            <i class="el-icon-user" />
          </div>
          <div class="sk-panel-info">
            <p class="sk-panel-title">{{ studentData.studentName }}</p>
            <p class="sk-panel-subtitle">{{ yearMonthDisplay }}</p>
          </div>
        </div>
      </div>

      <!-- Main Evaluation Content -->
      <div class="evl-detail-content-wrap sk-u-mt-12">
        <!-- 1. DAILY EVALUATIONS -->
        <div v-if="detailTab === 'daily'">
          <div v-if="dailyEvaluations.length === 0" class="evl-detail-empty">
            <i class="el-icon-document" />
            <span>Chưa có dữ liệu nhận xét</span>
          </div>

          <div
            v-for="item in dailyEvaluations"
            :key="item.uid"
            class="evl-detail-card"
          >
            <div class="evl-detail-card__header">
              <div class="evl-header-left">
                <el-tag size="mini" type="primary" effect="plain" class="evl-date-tag">
                  <i class="el-icon-date" /> Ngày {{ item.date | formatDate }}
                </el-tag>
              </div>
              <span class="evl-detail-teacher">
                <i class="el-icon-user-solid" /> {{ item.teacherName || item.createdBy || 'Giáo viên' }}
              </span>
            </div>

            <!-- DANH SÁCH CÁC MỤC NHẬN XÉT PHÂN THÀNH THẺ RIÊNG BIỆT -->
            <div v-if="item.items && item.items.length > 0" class="evl-category-grid">
              <div
                v-for="(catItem, cIdx) in item.items"
                :key="catItem.categoryId || cIdx"
                class="evl-cat-card"
              >
                <div class="evl-cat-card__header">
                  <div class="evl-cat-card__title">
                    <span class="evl-cat-dot" />
                    <span class="evl-cat-name">{{ catItem.categoryName }}</span>
                  </div>
                </div>
                <div class="evl-cat-card__body">
                  <div class="evl-cat-content-text">{{ catItem.content }}</div>
                </div>
              </div>
            </div>

            <!-- Fallback: daily cũ dạng 1 nội dung đơn -->
            <div v-else-if="item.content" class="evl-single-content-card">
              <div class="evl-single-content-header">
                <i class="el-icon-edit-outline" />
                <span>Nội dung nhận xét</span>
              </div>
              <div class="evl-single-content-body">{{ item.content }}</div>
            </div>

            <!-- Files đính kèm (nếu có) -->
            <div v-if="item.files && item.files.length > 0" class="evl-detail-files-section">
              <div class="evl-detail-files-title">
                <i class="el-icon-paperclip" /> File & hình ảnh đính kèm ({{ item.files.length }})
              </div>
              <div class="evl-detail-files-list">
                <div
                  v-for="(file, fIdx) in item.files"
                  :key="fIdx"
                  class="evl-detail-file-chip"
                  @click="openFilePreview(item.files)"
                >
                  <i :class="isImageFile(file) ? 'el-icon-picture' : 'el-icon-document'" />
                  <span class="evl-file-chip-name">{{ getFileName(file) }}</span>
                  <i class="el-icon-view evl-file-chip-action" />
                </div>
              </div>
            </div>

            <!-- Phản hồi (Replies) -->
            <div v-if="item.replies" class="evl-replies">
              <p class="evl-replies__title">
                <i class="el-icon-chat-dot-round" /> Phản hồi ({{ item.replies.length }})
              </p>
              <div v-if="item.replies.length > 0" class="evl-reply-list">
                <div
                  v-for="reply in item.replies"
                  :key="reply.uid"
                  class="evl-reply-item"
                >
                  <div class="evl-reply-item__meta">
                    <span class="evl-reply-item__name">{{ reply.replierName }}</span>
                    <el-tag size="mini" :type="roleTagType(reply.role)">{{ roleLabel(reply.role) }}</el-tag>
                    <span class="evl-reply-item__date">{{ reply.createdDate | formatDateTime }}</span>
                  </div>
                  <div class="evl-reply-item__content">{{ reply.content }}</div>
                </div>
              </div>
            </div>

            <!-- Add reply -->
            <div class="evl-add-reply">
              <el-input
                v-model="replyContents[item.uid]"
                size="small"
                placeholder="Viết phản hồi cho nhận xét này..."
                @keyup.enter.native="handleAddReply(item)"
              >
                <template #append>
                  <el-button
                    icon="el-icon-s-promotion"
                    :loading="replyLoading === item.uid"
                    @click="handleAddReply(item)"
                  />
                </template>
              </el-input>
            </div>
          </div>
        </div>

        <!-- 2. WEEKLY EVALUATIONS -->
        <div v-else-if="detailTab === 'weekly'">
          <div v-if="weeklyEvaluations.length === 0" class="evl-detail-empty">
            <i class="el-icon-document" />
            <span>Chưa có dữ liệu nhận xét</span>
          </div>

          <div
            v-for="item in weeklyEvaluations"
            :key="item.uid"
            class="evl-detail-card"
          >
            <div class="evl-detail-card__header">
              <div class="evl-header-left">
                <el-tag size="mini" type="primary" effect="plain" class="evl-date-tag">
                  <i class="el-icon-date" /> Tuần {{ item.weekNumber }}
                </el-tag>
                <span class="evl-date-range">{{ item.fromDate | formatDate }} – {{ item.toDate | formatDate }}</span>
              </div>
              <span class="evl-detail-teacher">
                <i class="el-icon-user-solid" /> {{ item.teacherName || item.createdBy || 'Giáo viên' }}
              </span>
            </div>

            <!-- Thẻ nội dung nhận xét tuần -->
            <div class="evl-single-content-card">
              <div class="evl-single-content-header">
                <i class="el-icon-edit-outline" />
                <span>Nội dung nhận xét tuần</span>
              </div>
              <div class="evl-single-content-body">{{ item.content }}</div>
            </div>

            <!-- Files đính kèm -->
            <div v-if="item.files && item.files.length > 0" class="evl-detail-files-section">
              <div class="evl-detail-files-title">
                <i class="el-icon-paperclip" /> File & hình ảnh đính kèm ({{ item.files.length }})
              </div>
              <div class="evl-detail-files-list">
                <div
                  v-for="(file, fIdx) in item.files"
                  :key="fIdx"
                  class="evl-detail-file-chip"
                  @click="openFilePreview(item.files)"
                >
                  <i :class="isImageFile(file) ? 'el-icon-picture' : 'el-icon-document'" />
                  <span class="evl-file-chip-name">{{ getFileName(file) }}</span>
                  <i class="el-icon-view evl-file-chip-action" />
                </div>
              </div>
            </div>

            <!-- Phản hồi -->
            <div v-if="item.replies" class="evl-replies">
              <p class="evl-replies__title">
                <i class="el-icon-chat-dot-round" /> Phản hồi ({{ item.replies.length }})
              </p>
              <div v-if="item.replies.length > 0" class="evl-reply-list">
                <div
                  v-for="reply in item.replies"
                  :key="reply.uid"
                  class="evl-reply-item"
                >
                  <div class="evl-reply-item__meta">
                    <span class="evl-reply-item__name">{{ reply.replierName }}</span>
                    <el-tag size="mini" :type="roleTagType(reply.role)">{{ roleLabel(reply.role) }}</el-tag>
                    <span class="evl-reply-item__date">{{ reply.createdDate | formatDateTime }}</span>
                  </div>
                  <div class="evl-reply-item__content">{{ reply.content }}</div>
                </div>
              </div>
            </div>

            <div class="evl-add-reply">
              <el-input
                v-model="replyContents[item.uid]"
                size="small"
                placeholder="Viết phản hồi cho nhận xét này..."
                @keyup.enter.native="handleAddReply(item)"
              >
                <template #append>
                  <el-button
                    icon="el-icon-s-promotion"
                    :loading="replyLoading === item.uid"
                    @click="handleAddReply(item)"
                  />
                </template>
              </el-input>
            </div>
          </div>
        </div>

        <!-- 3. MONTHLY EVALUATIONS -->
        <div v-else-if="detailTab === 'monthly'">
          <div v-if="monthlyEvaluations.length === 0" class="evl-detail-empty">
            <i class="el-icon-document" />
            <span>Chưa có dữ liệu nhận xét</span>
          </div>

          <div
            v-for="item in monthlyEvaluations"
            :key="item.uid"
            class="evl-detail-card"
          >
            <div class="evl-detail-card__header">
              <div class="evl-header-left">
                <el-tag size="mini" type="primary" effect="plain" class="evl-date-tag">
                  <i class="el-icon-date" /> {{ yearMonthDisplay || 'Tháng' }}
                </el-tag>
              </div>
              <span class="evl-detail-teacher">
                <i class="el-icon-user-solid" /> {{ item.createdBy || item.teacherName || 'Giáo viên' }}
              </span>
            </div>

            <!-- Thẻ nội dung nhận xét tháng -->
            <div class="evl-single-content-card">
              <div class="evl-single-content-header">
                <i class="el-icon-edit-outline" />
                <span>Nội dung nhận xét tháng</span>
              </div>
              <div class="evl-single-content-body">{{ item.content }}</div>
            </div>

            <!-- Files đính kèm -->
            <div v-if="item.files && item.files.length > 0" class="evl-detail-files-section">
              <div class="evl-detail-files-title">
                <i class="el-icon-paperclip" /> File & hình ảnh đính kèm ({{ item.files.length }})
              </div>
              <div class="evl-detail-files-list">
                <div
                  v-for="(file, fIdx) in item.files"
                  :key="fIdx"
                  class="evl-detail-file-chip"
                  @click="openFilePreview(item.files)"
                >
                  <i :class="isImageFile(file) ? 'el-icon-picture' : 'el-icon-document'" />
                  <span class="evl-file-chip-name">{{ getFileName(file) }}</span>
                  <i class="el-icon-view evl-file-chip-action" />
                </div>
              </div>
            </div>

            <!-- Phản hồi -->
            <div v-if="item.replies && item.replies.length > 0" class="evl-replies">
              <p class="evl-replies__title">
                <i class="el-icon-chat-dot-round" /> Phản hồi ({{ item.replies.length }})
              </p>
              <div class="evl-reply-list">
                <div
                  v-for="reply in item.replies"
                  :key="reply.uid"
                  class="evl-reply-item"
                >
                  <div class="evl-reply-item__meta">
                    <span class="evl-reply-item__name">{{ reply.replierName }}</span>
                    <el-tag size="mini" :type="roleTagType(reply.role)">{{ roleLabel(reply.role) }}</el-tag>
                    <span class="evl-reply-item__date">{{ reply.createdDate | formatDateTime }}</span>
                  </div>
                  <div class="evl-reply-item__content">{{ reply.content }}</div>
                </div>
              </div>
            </div>

            <div class="evl-add-reply">
              <el-input
                v-model="replyContents[item.uid]"
                size="small"
                placeholder="Viết phản hồi cho nhận xét này..."
                @keyup.enter.native="handleAddReply(item)"
              >
                <template #append>
                  <el-button
                    icon="el-icon-s-promotion"
                    :loading="replyLoading === item.uid"
                    @click="handleAddReply(item)"
                  />
                </template>
              </el-input>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- File preview dialog integration -->
    <EvaluationFilePreviewDialog ref="filePreviewDialog" />
  </DialogBase>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import EvaluationService from "@/modules/evaluation/services/EvaluationService";
import EvaluationFilePreviewDialog from "@/modules/evaluation/components/dialog/EvaluationFilePreviewDialog.vue";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import Vue from "vue";
import moment from "moment";

const ROLE_MAP = { parent: "Phụ huynh", teacher: "Giáo viên", plus: "Nhà trường" };
const ROLE_TAG_TYPE = { parent: "warning", teacher: "", plus: "success" };

export default {
  name: "EvaluationDetailDialog",
  components: { DialogBase, EvaluationFilePreviewDialog },
  props: {
    classId: { type: [Number, String], default: "" },
    date: { type: String, default: "" },
  },
  data() {
    return {
      visible: false,
      detailTab: "daily",
      yearMonth: "",
      studentData: null,
      dailyEvaluations: [],
      weeklyEvaluations: [],
      monthlyEvaluations: [],
      replyContents: {},
      replyLoading: null,
    };
  },
  computed: {
    schoolId() {
      return this.$store.state.auth.user.schoolId;
    },
    currentUserId() {
      return this.$store.state.auth.user.id;
    },
    currentUserName() {
      return this.$store.state.auth.user.fullName;
    },
    currentUserRole() {
      const appType = this.$store.state.auth.user.appType;
      if (appType === "teacher") return "TEACHER";
      return "SCHOOL";
    },
    dialogTitle() {
      switch (this.detailTab) {
        case "daily":
          return "Chi tiết nhận xét ngày";
        case "weekly":
          return "Chi tiết nhận xét tuần";
        case "monthly":
          return "Chi tiết nhận xét tháng";
        default:
          return "Chi tiết nhận xét";
      }
    },
    yearMonthDisplay() {
      if (!this.date) return "";

      switch (this.detailTab) {
        case "daily":
          return this.displayDateDaily();
        case "weekly":
          return this.displayDateWeekly();
        default:
          return this.displayDateMonthly();
      }
    },
  },
  filters: {
    formatDate(val) {
      if (!val) return "";
      const parts = val.split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      return val;
    },
    formatDateTime(val) {
      if (!val) return "";
      const dt = val.split("T");
      if (dt.length === 2) {
        const parts = dt[0].split("-");
        return `${dt[1].substring(0, 5)} ${parts[2]}/${parts[1]}`;
      }
      return val;
    },
  },
  methods: {
    async open(row, type) {
      this.studentData = row;
      this.detailTab = this._mapTypeToTab(type);
      this.replyContents = {};
      this.visible = true;
      await this._loadStudentDetail(row.studentId);
    },

    _mapTypeToTab(type) {
      const map = { DAILY: "daily", WEEKLY: "weekly", MONTHLY: "monthly" };
      return map[type] || "daily";
    },

    async _loadStudentDetail(studentId) {
      try {
        const params = {
          schoolId: this.schoolId,
          classId: this.classId,
          studentId: studentId,
          date: this._getRequestDate(),
        };
        const res = await EvaluationService.getByStudent(params);
        const data = res.data.data;
        this.yearMonth = data.yearMonth || "";
        this.dailyEvaluations = data.dailyEvaluations || [];
        this.weeklyEvaluations = data.weeklyEvaluations || [];
        this.monthlyEvaluations = data.monthlyEvaluations || [];
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải chi tiết nhận xét!"
        );
      }
    },

    async handleAddReply(evaluation) {
      const content = (this.replyContents[evaluation.uid] || "").trim();
      if (!content) return;

      this.replyLoading = evaluation.uid;
      try {
        const payload = {
          classId: this.classId,
          studentId: this.studentData.studentId,
          date: evaluation.date || evaluation.fromDate || this._getRequestDate(),
          evaluationUid: evaluation.uid,
          evaluationType:
            evaluation.type ||
            evaluation.evaluationType ||
            { daily: "DAILY", weekly: "WEEKLY", monthly: "MONTHLY" }[
              this.detailTab
            ],
          content,
        };

        const res = await EvaluationService.createReply(payload);
        const newReply = res.data.data;
        if (!evaluation.replies) {
          this.$set(evaluation, "replies", []);
        }
        evaluation.replies.push(newReply);
        this.$set(this.replyContents, evaluation.uid, "");
        NotifyMessage.leftCornerNotificationSuccess("Gửi phản hồi thành công!");
        this.$emit("updated");
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể gửi phản hồi!"
        );
      } finally {
        this.replyLoading = null;
      }
    },

    openFilePreview(files) {
      this.$refs.filePreviewDialog.open(files);
    },

    isImageFile(file) {
      const name = String(typeof file === "string" ? file : file.objectKey || file.name || "").toLowerCase();
      const ext = name.split(".").pop();
      return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
    },

    getFileName(file) {
      const raw = String(typeof file === "string" ? file : file.objectKey || file.name || "");
      const name = raw.split("/").pop();
      const prefixMatch = name.match(/^evaluation-\d+_(.+)$/i);
      if (prefixMatch && prefixMatch[1]) {
        return prefixMatch[1].replace(/_/g, " ");
      }
      return name;
    },

    roleLabel(role) {
      return ROLE_MAP[role] || role;
    },

    roleTagType(role) {
      return ROLE_TAG_TYPE[role] || "info";
    },

    _getRequestDate() {
      if (this.detailTab === "monthly") {
        return moment(this.date, "YYYY-MM", true).isValid()
          ? moment(this.date, "YYYY-MM").startOf("month").format("YYYY-MM-DD")
          : this.date;
      }
      return this.date;
    },

    handleClose() {
      this.visible = false;
      this.studentData = null;
      this.dailyEvaluations = [];
      this.weeklyEvaluations = [];
      this.monthlyEvaluations = [];
    },

    displayDateDaily() {
      if (this.dailyEvaluations.length === 0) return "";
      const dailyEvaluation = this.dailyEvaluations[0];
      return `Ngày ${Vue.filter("formatDate")(dailyEvaluation.date)}`;
    },

    displayDateWeekly() {
      if (this.weeklyEvaluations.length === 0) return "";
      const weeklyEvaluation = this.weeklyEvaluations[0];
      return `Tuần ${weeklyEvaluation.weekNumber} (${Vue.filter("formatDate")(weeklyEvaluation.fromDate)} - ${Vue.filter("formatDate")(weeklyEvaluation.toDate)})`;
    },

    displayDateMonthly() {
      if (!this.yearMonth) return "";

      const month = moment(
        String(this.yearMonth).substring(0, 7),
        "YYYY-MM",
        true
      );
      return month.isValid()
        ? `Tháng ${month.format("MM/YYYY")}`
        : `Tháng ${this.yearMonth}`;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-dialog";

.evl-detail {
  padding: 4px 0;
  height: 70vh;
  max-height: 720px;
  min-height: 520px;
  display: flex;
  flex-direction: column;
}

.evl-detail-student-panel {
  background: #f8fafc;
  border-radius: 14px;
  flex-shrink: 0;
}

.evl-detail-content-wrap {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  margin-top: 12px;
}

.evl-detail-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 32px 16px;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;

  i {
    font-size: 22px;
  }
}

/* ─── Evaluation Card Outer ──────────────────────── */
.evl-detail-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    padding-bottom: 10px;
    border-bottom: 1px solid #e2e8f0;
  }
}

.evl-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.evl-date-tag {
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 6px;
  padding: 2px 8px;
}

.evl-date-range {
  font-size: 12.5px;
  color: #64748b;
  font-weight: 500;
}

.evl-detail-teacher {
  font-size: 12.5px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    color: #2563eb;
  }
}

/* ─── Distinct Category Cards (1 thẻ ngang mỗi hàng, chiều cao 84px - 200px) ── */
.evl-category-grid {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
  width: 100%;
}

.evl-cat-card {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.03);
  transition: all 0.2s ease;
  width: 100%;
  min-height: 84px;
  max-height: 200px;
  flex-shrink: 0;

  &:hover {
    border-color: #93c5fd;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.08);
  }

  &__header {
    width: 190px;
    min-width: 190px;
    max-width: 190px;
    background: #f8fafc;
    border-right: 1px solid #e2e8f0;
    border-bottom: none;
    padding: 8px 14px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    flex-shrink: 0;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  &__badge {
    font-size: 10.5px;
    font-weight: 600;
    color: #64748b;
    background: #ffffff;
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
    align-self: flex-start;
  }

  &__body {
    flex: 1;
    min-width: 0;
    padding: 8px 16px;
    background: #ffffff;
    display: flex;
    align-items: center;
    overflow-y: auto;
  }
}

.evl-cat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2563eb;
  display: inline-block;
  flex-shrink: 0;
}

.evl-cat-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.4;
}

.evl-cat-content-text {
  font-size: 13.5px;
  color: #334155;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
  width: 100%;
}

/* ─── Single Content Card (Weekly/Monthly) ───────── */
.evl-single-content-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  min-height: 84px;
}

.evl-single-content-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;

  i {
    color: #2563eb;
    font-size: 14px;
  }
}

.evl-single-content-body {
  padding: 14px;
  font-size: 14px;
  color: #1e293b;
  line-height: 1.65;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
}

/* ─── File Attachments Section ──────────────────── */
.evl-detail-files-section {
  margin: 12px 0 14px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.evl-detail-files-title {
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;

  i {
    color: #2563eb;
  }
}

.evl-detail-files-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.evl-detail-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  font-size: 12.5px;
  color: #1e40af;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #dbeafe;
    border-color: #93c5fd;
  }

  .evl-file-chip-action {
    color: #3b82f6;
    margin-left: 2px;
  }
}

/* ─── Replies Section ───────────────────────────── */
.evl-replies {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #cbd5e1;

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    margin: 0 0 10px;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      color: #2563eb;
      font-size: 15px;
    }
  }
}

.evl-reply-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.evl-reply-item {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;

  &__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  &__name {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
  }

  &__date {
    font-size: 11.5px;
    color: #94a3b8;
    margin-left: auto;
  }

  &__content {
    font-size: 13px;
    color: #475569;
    line-height: 1.55;
  }
}

.evl-add-reply {
  margin-top: 12px;

  /deep/ .el-input__inner {
    border-radius: 8px 0 0 8px;
  }

  /deep/ .el-input-group__append {
    border-radius: 0 8px 8px 0;
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;

    &:hover {
      background: #1d4ed8;
    }
  }
}
</style>
